import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

import logo from '../assets/logo.svg';
import hashtagTitle from '../assets/title.png';
import './Navbar.css';

const Navbar = () => {
    const [isScrolled, setIsScrolled] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const location = useLocation();
    const isHome = location.pathname === '/';

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 20);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Prevent body scroll when menu is open
    useEffect(() => {
        if (isMobileMenuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
    }, [isMobileMenuOpen]);

    const isActive = (path) => location.pathname === path;

    return (
        <nav className={`navbar ${isScrolled ? 'navbar-scrolled' : ''} ${isHome && !isScrolled ? 'navbar-transparent' : ''}`}>
            <div className="container navbar-container">
                <Link to="/" className="navbar-brand">
                    <img src={logo} alt="Hashtag PG Logo" className="navbar-logo" />
                    <img src={hashtagTitle} alt="Hashtag PG" className="navbar-title" />
                </Link>

                {/* Desktop Menu */}
                <div className="navbar-menu">
                    <Link
                        to="/"
                        className={`navbar-link ${isActive('/') ? 'navbar-link-active' : ''}`}
                    >
                        Home
                    </Link>
                    <Link
                        to="/about"
                        className={`navbar-link ${isActive('/about') ? 'navbar-link-active' : ''}`}
                    >
                        About
                    </Link>
                    <Link
                        to="/listings"
                        className={`navbar-link ${isActive('/listings') ? 'navbar-link-active' : ''}`}
                    >
                        Collection
                    </Link>
                    <Link
                        to="/contact"
                        className={`navbar-link ${isActive('/contact') ? 'navbar-link-active' : ''}`}
                    >
                        Contact
                    </Link>
                </div>

                {/* Mobile Menu Button */}
                <button
                    className="navbar-toggle"
                    onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                    aria-label="Toggle menu"
                >
                    {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
                </button>
            </div>

            {/* Mobile Menu Overlay */}
            <AnimatePresence>
                {isMobileMenuOpen && (
                    <motion.div
                        className="navbar-mobile-menu"
                        initial={{ opacity: 0, x: '100%' }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: '100%' }}
                        transition={{ type: 'tween', duration: 0.3 }}
                    >
                        <motion.div
                            initial={{ y: 20, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ delay: 0.1 }}
                        >
                            <Link
                                to="/"
                                onClick={() => setIsMobileMenuOpen(false)}
                                className={`navbar-mobile-link ${isActive('/') ? 'navbar-mobile-link-active' : ''}`}
                            >
                                Home
                            </Link>
                        </motion.div>
                        <motion.div
                            initial={{ y: 20, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ delay: 0.2 }}
                        >
                            <Link
                                to="/about"
                                onClick={() => setIsMobileMenuOpen(false)}
                                className={`navbar-mobile-link ${isActive('/about') ? 'navbar-mobile-link-active' : ''}`}
                            >
                                About
                            </Link>
                        </motion.div>
                        <motion.div
                            initial={{ y: 20, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ delay: 0.3 }}
                        >
                            <Link
                                to="/listings"
                                onClick={() => setIsMobileMenuOpen(false)}
                                className={`navbar-mobile-link ${isActive('/listings') ? 'navbar-mobile-link-active' : ''}`}
                            >
                                Collection
                            </Link>
                        </motion.div>
                        <motion.div
                            initial={{ y: 20, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ delay: 0.4 }}
                        >
                            <Link
                                to="/contact"
                                onClick={() => setIsMobileMenuOpen(false)}
                                className={`navbar-mobile-link ${isActive('/contact') ? 'navbar-mobile-link-active' : ''}`}
                            >
                                Contact
                            </Link>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </nav>
    );
};

export default Navbar;
